import React, { useRef, useEffect, useCallback } from "react";

function spawnShooter(w, h) {
  const angle = Math.PI / 6 + Math.random() * (Math.PI / 4);
  const speed = 8 + Math.random() * 12;
  const len = 60 + Math.random() * 100;
  return {
    x: Math.random() * w * 1.2 - w * 0.1,
    y: -20,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    len,
    life: 1,
    decay: 0.008 + Math.random() * 0.006,
    width: 1 + Math.random() * 0.8,
  };
}

function StarsCanvas({ darkMode }) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const darkRef = useRef(darkMode);

  useEffect(() => {
    darkRef.current = darkMode;
  }, [darkMode]);

  const createStars = useCallback((w, h) => {
    const count = Math.min(Math.floor((w * h) / 6000), 200);
    return Array.from({ length: count }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      r: Math.random() * 1.2 + 0.2,
      o: Math.random() * 0.35 + 0.05,
      speed: Math.random() * 0.2 + 0.03,
      phase: Math.random() * Math.PI * 2,
    }));
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let w = (canvas.width = window.innerWidth);
    let h = (canvas.height = window.innerHeight);
    let stars = createStars(w, h);
    let shooters = [];
    let nextSpawn = 3000 + Math.random() * 5000;
    let elapsed = 0;
    let lastTime = performance.now();

    const onResize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      stars = createStars(w, h);
    };
    window.addEventListener("resize", onResize);

    let mouseX = 0;
    let mouseY = 0;
    let targetMouseX = 0;
    let targetMouseY = 0;

    const onMouseMove = (e) => {
      targetMouseX = (e.clientX / w - 0.5) * 35;
      targetMouseY = (e.clientY / h - 0.5) * 35;
    };
    window.addEventListener("mousemove", onMouseMove, { passive: true });

    let t = 0;
    const draw = (now) => {
      const dt = now - lastTime;
      lastTime = now;
      elapsed += dt;

      // Smooth mouse lerp
      mouseX += (targetMouseX - mouseX) * 0.05;
      mouseY += (targetMouseY - mouseY) * 0.05;

      ctx.clearRect(0, 0, w, h);
      t += 0.008;

      const dark = darkRef.current;
      const starR = dark ? 200 : 80;
      const starG = dark ? 200 : 80;
      const starB = dark ? 200 : 80;

      for (const s of stars) {
        const flicker = Math.sin(t * s.speed * 8 + s.phase) * 0.3 + 0.7;
        const px = s.x + mouseX * (s.r * 0.8);
        const py = s.y + mouseY * (s.r * 0.8);

        ctx.beginPath();
        ctx.arc(px, py, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${starR}, ${starG}, ${starB}, ${s.o * flicker})`;
        ctx.fill();
      }

      if (elapsed >= nextSpawn) {
        shooters.push(spawnShooter(w, h));
        elapsed = 0;
        nextSpawn = 3000 + Math.random() * 5000;
      }

      const headR = dark ? 255 : 40;
      const headG = dark ? 255 : 40;
      const headB = dark ? 255 : 50;
      const trailR = dark ? 200 : 100;
      const trailG = dark ? 200 : 100;
      const trailB = dark ? 220 : 120;

      for (let i = shooters.length - 1; i >= 0; i--) {
        const s = shooters[i];
        s.x += s.vx;
        s.y += s.vy;
        s.life -= s.decay;

        if (s.life <= 0 || s.x > w + 100 || s.y > h + 100) {
          shooters.splice(i, 1);
          continue;
        }

        const mag = Math.sqrt(s.vx * s.vx + s.vy * s.vy);
        const tailX = s.x - (s.vx / mag) * s.len;
        const tailY = s.y - (s.vy / mag) * s.len;

        const grad = ctx.createLinearGradient(s.x, s.y, tailX, tailY);
        grad.addColorStop(0, `rgba(${headR}, ${headG}, ${headB}, ${0.8 * s.life})`);
        grad.addColorStop(0.3, `rgba(${trailR}, ${trailG}, ${trailB}, ${0.3 * s.life})`);
        grad.addColorStop(1, `rgba(${trailR}, ${trailG}, ${trailB}, 0)`);

        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(tailX, tailY);
        ctx.strokeStyle = grad;
        ctx.lineWidth = s.width;
        ctx.lineCap = "round";
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(s.x, s.y, s.width + 0.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${headR}, ${headG}, ${headB}, ${0.9 * s.life})`;
        ctx.fill();
      }

      animationRef.current = requestAnimationFrame(draw);
    };
    animationRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMouseMove);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [createStars]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-0 pointer-events-none"
    />
  );
}

export default React.memo(StarsCanvas);
